import { useState, useEffect, useRef } from "react";
import { motion as Motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { validarRUT } from "@/utils/rutValidator";
import FormInput from "./FormInput";
import FormSelect from "./FormSelect";
import FormRadio from "./FormRadio";
import FormCheckbox from "./FormCheckbox";
import FormTextarea from "./FormTextarea";
import FormFileUpload from "./FormFileUpload";
import { Link } from "react-router-dom";
import { useEmpresaForm } from "@/hooks/useEmpresaForm";

export default function StepsForm({ steps = [], onComplete, initialData = null, isLoading = false, onFieldChange }) {
  const { 
    formData, 
    currentStep, 
    errors, 
    updateFormData, 
    setCurrentStep, 
    setErrors,
    loadExistingData 
  } = useEmpresaForm();

  const totalSteps = steps.length;
  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;

  // Cargar datos iniciales una sola vez (cuando llegan por primera vez con contenido)
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!hasInitializedRef.current && initialData && Object.keys(initialData).length > 0) {
      loadExistingData(initialData);
      hasInitializedRef.current = true;
    }
  }, [initialData, loadExistingData]);

  // Handle field change
  const handleFieldChange = (fieldName, value) => {
    updateFormData(fieldName, value);
    
    // Notificar al componente padre sobre cambios en campos específicos
    if (onFieldChange) {
      onFieldChange(fieldName, value);
    }
    
    // Clear error when user starts typing
    if (errors[fieldName]) {
      const newErrors = { ...errors };
      delete newErrors[fieldName];
      setErrors(newErrors);
    }
  };

  // Validate current step
  const validateCurrentStep = () => {
    const currentStepData = steps[currentStep];
    const newErrors = {};

    currentStepData.fields.forEach((field) => {
      const value = formData[field.name];
      
      // Check if field is required
      if (field.required) {
        // For text/textarea fields - check if empty or only whitespace
        if (field.type === "text" || field.type === "textarea" || field.type === "email" || field.type === "password") {
          if (!value || (typeof value === "string" && value.trim() === "")) {
            newErrors[field.name] = "Este campo es requerido";
          }
        }
        // For number fields
        else if (field.type === "number") {
          if (value === undefined || value === null || value === "") {
            newErrors[field.name] = "Este campo es requerido";
          }
        }
        // For file fields
        else if (field.type === "file") {
          if (!value) {
            newErrors[field.name] = "Debes subir un archivo";
          }
        }
        // For select fields
        else if (field.type === "select") {
          if (!value || value === "") {
            newErrors[field.name] = "Debes seleccionar una opción";
          }
        }
        // For radio fields
        else if (field.type === "radio") {
          if (!value || value === "") {
            newErrors[field.name] = "Debes seleccionar una opción";
          }
        }
        // For checkbox fields
        else if (field.type === "checkbox") {
          if (!value || (Array.isArray(value) && value.length === 0)) {
            newErrors[field.name] = "Debes seleccionar al menos una opción";
          }
        }
        // For other types, just check if value exists
        else if (!value) {
          newErrors[field.name] = "Este campo es requerido";
        }
      }
      
      // Email validation (only if field has value)
      if (field.type === "email" && value && typeof value === "string") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value.trim())) {
          newErrors[field.name] = "Correo electrónico inválido";
        }
      }

      // Number validation (only if field has value)
      if (field.type === "number" && value !== undefined && value !== null && value !== "") {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          newErrors[field.name] = "Debe ser un número válido";
        }
        // Check min value
        if (field.min !== undefined && numValue < field.min) {
          newErrors[field.name] = `El valor mínimo es ${field.min}`;
        }
        // Check max value
        if (field.max !== undefined && numValue > field.max) {
          newErrors[field.name] = `El valor máximo es ${field.max}`;
        }
      }

      // RUT validation for specific fields (only if field has value)
      const RUT_FIELD_NAMES = ["rut", "rut_text", "numeroTributario"];
      if (RUT_FIELD_NAMES.includes(field.name) && value && typeof value === "string") {
        const rutLimpio = value.replace(/\./g, "").replace(/-/g, "");
        if (rutLimpio.length >= 8 && !validarRUT(value)) {
          newErrors[field.name] = "RUT inválido";
        }
      }

      // Custom field validation (only if field has value and no prior error)
      if (field.validate && value && !newErrors[field.name]) {
        const customError = field.validate(value);
        if (customError) {
          newErrors[field.name] = customError;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next step
  const handleNext = () => {
    if (validateCurrentStep()) {
      if (isLastStep) {
        // Complete form
        if (onComplete) {
          onComplete(formData);
        }
      } else {
        setCurrentStep(currentStep + 1);
        // Scroll to top when moving to next step
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      // Scroll to first error
      setTimeout(() => {
        const firstError = document.querySelector('[class*="border-red"]');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  // Handle previous step
  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  // Render field based on type
  const renderField = (field) => {
    const value = formData[field.name];
    const error = errors[field.name];

    const commonProps = {
      field,
      value,
      error,
      onChange: handleFieldChange,
    };

    switch (field.type) {
      case "text":
      case "email":
      case "number":
      case "date":
      case "password":
        return <FormInput key={field.name} {...commonProps} />;
      case "select":
        return <FormSelect key={field.name} {...commonProps} />;
      case "radio":
        return <FormRadio key={field.name} {...commonProps} />;
      case "checkbox":
        return <FormCheckbox key={field.name} {...commonProps} />;
      case "textarea":
        return <FormTextarea key={field.name} {...commonProps} />;
      case "file":
        return <FormFileUpload key={field.name} {...commonProps} />;
      default:
        return <FormInput key={field.name} {...commonProps} />;
    }
  };

  if (!steps || steps.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        No se han configurado pasos para este formulario
      </div>
    );
  }

  const currentStepData = steps[currentStep];
  return (
    <div className="w-full max-w-4xl mx-auto sm:max-h-screen bg-aw-bluedark pb-2">
      {/* back icon in mobile */}
      <div className="group/back relative flex justify-start w-full">
        <button onClick={() => window.history.back()} className="peer group/link absolute w-8 h-8 top-1 left-5 flex items-center justify-center rounded-full border-2 border-gray-300 hover:border-aw-green transition-all duration-200">
          <FontAwesomeIcon
            icon="fa-solid fa-arrow-left"
            className="text-gray-300 group-hover/link:text-aw-green duration-300"
          />
        </button>
        <div className="flex flex-row items-center justify-end duration-300 transition-all py-3 p-4 mb-2 ml-3 w-11/12 peer-hover:w-5/12 bg-aw-blueblack rounded-full text-xs">
          <h4 className="font-bold text-gray-300 group-hover/back:text-aw-green duration-300">Regresar</h4>
        </div>
      </div>
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-4 px-4">
          {steps.map((step, index) => (
            <div
              key={index}
              className="flex items-center flex-1 last:flex-none"
            >
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
                  index < currentStep
                    ? "bg-aw-green border-aw-green text-aw-bluedark"
                    : index === currentStep
                    ? "bg-aw-bluedark border-aw-green text-white"
                    : "bg-white border-gray-300 text-gray-600"
                }`}
              >
                {index < currentStep ? (
                  <FontAwesomeIcon icon="fa-solid fa-check" />
                ) : (
                  <span className="font-semibold">{index + 1}</span>
                )}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-1 mx-2 transition-all duration-300 ${
                    index < currentStep ? "bg-aw-green" : "bg-gray-300"
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        {/* <div className="text-center">
          <p className="text-sm text-gray-500">
            Paso {currentStep + 1} de {totalSteps}
          </p>
        </div> */}
      </div>

      {/* Form Content */}
      <div className="rounded-lg shadow-lg px-8 max-h-[88vh] sm:max-h-[85vh] overflow-auto scrollbar-box thin">
        <AnimatePresence mode="wait">
          <Motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            {/* Step Header */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">
                {currentStepData.title}
              </h2>
              {currentStepData.description && (
                <p className="text-aw-green-75 leading-4 text-sm">{currentStepData.description}</p>
              )}
            </div>

            {/* Step Fields */}
            <div className="space-y-4">
              {currentStepData.fields.map((field) => renderField(field))}
            </div>
          </Motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="flex flex-col gap-4 mt-8 pt-6 border-t border-gray-200">
          {/* Error Summary */}
          {Object.keys(errors).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <FontAwesomeIcon icon="fa-solid fa-circle-exclamation" className="text-red-500 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-700">
                  Hay {Object.keys(errors).length} campo{Object.keys(errors).length > 1 ? 's' : ''} con errores
                </p>
                <p className="text-xs text-red-600 mt-1">
                  Por favor, revisa y completa los campos requeridos antes de continuar.
                </p>
              </div>
            </div>
          )}
          
          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={isFirstStep}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold transition-all duration-300 ${
                isFirstStep
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-gray-500 text-white hover:bg-gray-600 hover:scale-95"
              }`}
            >
              <FontAwesomeIcon icon="fa-solid fa-arrow-left" />
              Anterior
            </button>

            <button
              type="button"
              onClick={handleNext}
              disabled={isLoading}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold transition-all duration-300 ${
                isLoading 
                  ? "bg-gray-400 text-gray-600 cursor-not-allowed" 
                  : "bg-aw-green text-aw-bluedark hover:scale-95"
              }`}
            >
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon="fa-solid fa-spinner" className="animate-spin" />
                  Procesando...
                </>
              ) : isLastStep ? (
                <>
                  Finalizar
                  <FontAwesomeIcon icon="fa-solid fa-check" />
                </>
              ) : (
                <>
                  Siguiente
                  <FontAwesomeIcon icon="fa-solid fa-arrow-right" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
